<script>
import Layout from "../../layouts/main";
import PageHeader from "@/components/page-header";
import { sessionHelper, sharedHelper } from "@/utils";
import { SESSION, DATE } from "@/constants";
import { required } from "vuelidate/lib/validators";
import { mapState } from "vuex";
import moment from "moment";
import i18n from "@/i18n";
import { DOMAIN_GROUP_MODULE } from '@/state/modules/domainGroup'
import { domainGroupMethods } from "@/state/helpers";

const slug = (value) => {
  if (value == null || value == '') {
    return true;
  }
  return /^[a-z0-9]+(-[a-z0-9]+)*$/.test(value);
};

export default {
  page: {
    title: i18n.t("label.updateFormX", [i18n.t("module.domainGroup")]),
  },
  components: { Layout, PageHeader },
  beforeRouteLeave(to, from, next) {
    if (to.name != "Domain-group-listing") {
      sessionHelper.clearSearch(SESSION.DOMAIN_GROUP_SEARCH);
    }
    next();
  },
  computed: {
    ...mapState(DOMAIN_GROUP_MODULE, ["status", "candidates"]),
    data() {
      return this.$store.state.domainGroup.details;
    },
    memberFields() {
      return [
        { key: "selected", label: i18n.t("label.members"), thStyle: { width: '90px' } },
        { key: "domain", label: i18n.t("label.domain") },
        { key: "countryCode", label: i18n.t("label.countryCode") },
        { key: "lastStatus", label: i18n.t("label.lastStatus") },
        { key: "role", label: i18n.t("label.role"), thStyle: { width: '150px' } },
      ];
    },
    visibleMemberRows() {
      const needle = (this.memberFilter || '').trim().toLowerCase();

      return needle === ''
        ? this.memberRows
        : this.memberRows.filter((r) => r.domain.toLowerCase().indexOf(needle) !== -1);
    },
    selectedMembers() {
      // The primary first, then the backups in the order they are listed.
      const chosen = this.memberRows.filter((r) => r.selected);
      const primary = chosen.filter((r) => r.uuid === this.primaryUuid);
      const backups = chosen.filter((r) => r.uuid !== this.primaryUuid);

      return primary.concat(backups);
    },
  },
  watch: {
    memberFilter() { this.memberPage = 1; },
    // Membership rows come from two calls (group show + candidates); rebuild
    // whenever either lands so the table reflects the latest server state.
    candidates() { this.buildMemberRows(); },
    'data.id'() { this.buildMemberRows(); },
    'data.domains'() { this.buildMemberRows(); },
  },
  data() {
    return {
      primaryUuid: null,
      memberFilter: "",
      memberPage: 1,
      memberPerPage: 25,
      title: i18n.t("label.updateFormX", [i18n.t("module.domainGroup")]),
      items: [
        {
          text: i18n.t("module.dashboard"),
          href: "/",
        },
        {
          text: i18n.t("module.domainGroup"),
          href: "/domain-group",
        },
        {
          text: i18n.t("label.updateX", [i18n.t("module.domainGroup")]),
          active: true,
        },
      ],
      submitted: false,
      permissions: sessionHelper.getPermissions(),
      updatePermission: null,
      readPermission: null,
      memberRows: [],
    };
  },
  validations: {
    data: {
      name: { required },
      slug: { slug },
    },
  },
  async created() {
    this.searchCriteria = sessionHelper.retrieveSearch(SESSION.DOMAIN_GROUP_SEARCH);
    await this.validPermissions();
    this.getDomainGroupDetails();
  },
  methods: {
    ...domainGroupMethods,
    async validPermissions() {
      var jsonPermissions = JSON.parse(this.permissions)
      this.updatePermission = jsonPermissions.some(p => p.code === "DOMAIN_UPDATE");
      this.readPermission = jsonPermissions.some(p => p.code === "DOMAIN_READ");
    },
    getDomainGroupDetails() {
      if (this.readPermission == true) {
        const uuid = this.searchCriteria.selectedRecordId;
        this.getDomainGroup({ uuid });
        this.getDomainGroupCandidates({ uuid });
      }
    },
    buildMemberRows() {
      const current = {};
      (this.data.domains || []).forEach((d) => { current[d.uuid] = d; });

      const rows = (this.candidates || []).map((c) => {
        const member = current[c.uuid];
        return {
          uuid: c.uuid,
          domain: c.domain,
          countryCode: c.countryCode,
          lastStatus: c.lastStatus,
          selected: !!member,
          priority: member ? member.priority : (c.priority != null ? c.priority : 100),
          // Belongs to another group: selecting it here moves it.
          otherGroupName: c.domainGroupUuid && c.domainGroupUuid !== this.data.id ? c.domainGroupName : null,
        };
      });

      // The lowest priority among current members is the primary.
      const members = (this.data.domains || []).slice().sort((a, b) => a.priority - b.priority);
      this.primaryUuid = members.length > 0 ? members[0].uuid : null;

      // Members the candidates call did not return (e.g. inactive) still need a row.
      (this.data.domains || []).forEach((d) => {
        if (!rows.some((r) => r.uuid === d.uuid)) {
          rows.push({
            uuid: d.uuid,
            domain: d.domain,
            countryCode: d.countryCode,
            lastStatus: d.lastStatus,
            selected: true,
            priority: d.priority,
            otherGroupName: null,
          });
        }
      });

      this.memberRows = rows;
    },
    normalizeSlug() {
      this.data.slug = (this.data.slug || '').trim().toLowerCase();
    },
    requestUpdate() {
      this.normalizeSlug();
      this.submitted = true;
      this.$v.$touch();

      if (this.$v.$invalid) {
        return;
      } else {
        let data = {
          id: this.data.id,
          name: this.data.name,
          slug: this.data.slug,
        };
        if (this.updatePermission == true) {
          this.updateDomainGroup({ data });
        }
      }
    },
    requestSaveMembers() {
      if (this.updatePermission != true) {
        return;
      }
      const chosen = this.selectedMembers;
      if (chosen.length > 0 && !chosen.some((m) => m.uuid === this.primaryUuid)) {
        sharedHelper.alertErrorMessageWithoutCode(this.$t("message.pickAPrimary"));
        return;
      }
      // Priority is an implementation detail: the primary is 1, the backups
      // follow in the order they appear.
      const domains = chosen.map((m, index) => {
        return { uuid: m.uuid, priority: index + 1 };
      });
      this.updateDomainGroupDomains({ uuid: this.data.id, domains });
    },
    statusVariant(status) {
      return {
        OK: 'success',
        SUSPECT: 'warning',
        PARTIAL: 'warning',
        BLOCKED: 'danger',
        REAL_BLOCK: 'danger',
        ERROR_BLOCK: 'warning',
        REGISTRAR_SUSPENDED: 'dark',
        NOT_REGISTERED: 'dark',
        EXPIRED: 'dark',
        UNRESOLVABLE: 'secondary',
        SITE_DOWN: 'secondary',
        BROWSER_WARNING: 'warning',
        NO_DATA: 'light',
        ERROR: 'danger',
      }[status] || 'secondary';
    },
    statusLabel(status) {
      return this.$te('enumDomainStatus.' + status) ? this.$t('enumDomainStatus.' + status) : status;
    },
    copyLink(link) {
      navigator.clipboard.writeText(link)
        .then(() => sharedHelper.alertSuccessMessage(this.$t("message.copied")));
    },
    formatDateTime(value) {
      return value ? moment(value).format(DATE.DATE_TIME_SHORT_TZ) : '-';
    },
  },
};
</script>

<template>
  <Layout>
    <PageHeader :title="title" :items="items" />
    <div class="row">
      <div class="col">
        <div class="card">
          <div class="card-body">
            <form @submit.prevent="requestUpdate" v-if="readPermission == true">
              <b-form-group id="name" label-cols-sm="2" label-cols-lg="2" :label="$t('label.name')" label-for="name">
                <b-form-input for="name" v-model.trim="data.name"
                  :class="{ 'is-invalid': submitted && !$v.data.name.required }"></b-form-input>
                <div v-if="submitted && !$v.data.name.required" class="invalid-feedback">
                  <span>{{ $t("message.requiredX", [$t("label.name")]) }}</span>
                </div>
              </b-form-group>

              <b-form-group id="slug" label-cols-sm="2" label-cols-lg="2" :label="$t('label.slug')" label-for="slug">
                <b-form-input for="slug" v-model.trim="data.slug" @change="normalizeSlug"
                  :class="{ 'is-invalid': submitted && !$v.data.slug.slug }"></b-form-input>
                <div v-if="submitted && !$v.data.slug.slug" class="invalid-feedback">
                  <span>{{ $t("message.validX", [$t("label.slug")]) }}</span>
                </div>
                <small class="form-text text-muted">{{ $t('message.slugHelp') }}</small>
              </b-form-group>

              <b-form-group id="countryCode" label-cols-sm="2" label-cols-lg="2" :label="$t('label.countryCode')">
                <b-form-input :value="data.countryCode" plaintext readonly></b-form-input>
                <small class="form-text text-muted">{{ $t('message.groupCountryFixedHelp') }}</small>
              </b-form-group>


              <div class="mt-4 text-center" style="float: right">
                <button class="btn btn-info w-md waves-effect waves-light" style="margin-right: 10px" type="submit"
                  :disabled="status.processing || updatePermission != true">
                  {{ $t("label.updateX", [$t("module.domainGroup")]) }}
                </button>
              </div>
            </form>
            <div style="text-align: center;" v-else>
              {{ $t('message.noPermissionMessage') }}
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="row" v-if="readPermission == true && data.id">
      <div class="col-lg-12">
        <div class="card">
          <div class="card-body">
            <h4 class="card-title">{{ $t('label.members') }}</h4>
            <p class="card-title-desc">{{ $t('message.membersHelp') }}</p>

            <div class="table-responsive mb-0">
              <div class="row mb-2">
                <div class="col-sm-6 col-md-4">
                  <b-form-input v-model="memberFilter" type="search" size="sm"
                    :placeholder="$t('label.search')"></b-form-input>
                </div>
                <div class="col text-right text-muted small align-self-center">
                  {{ $t('label.selectedXofY', [selectedMembers.length, memberRows.length]) }}
                </div>
              </div>

              <b-table :items="visibleMemberRows" :fields="memberFields" responsive="sm" small show-empty
                :per-page="memberPerPage" :current-page="memberPage"
                :empty-text="$t('message.noCandidates')">
                <template #cell(selected)="row">
                  <b-form-checkbox v-model="row.item.selected" :disabled="updatePermission != true"></b-form-checkbox>
                </template>

                <template #cell(domain)="row">
                  {{ row.item.domain }}
                  <div class="text-muted small" v-if="row.item.otherGroupName">
                    {{ $t('label.inGroupX', [row.item.otherGroupName]) }}
                  </div>
                </template>

                <template #cell(lastStatus)="row">
                  <b-badge :variant="statusVariant(row.item.lastStatus)" v-if="row.item.lastStatus">
                    {{ statusLabel(row.item.lastStatus) }}
                  </b-badge>
                  <span v-else>-</span>
                </template>

                <template #cell(role)="row">
                  <b-form-radio v-model="primaryUuid" :value="row.item.uuid"
                    :disabled="!row.item.selected || updatePermission != true">
                    {{ primaryUuid === row.item.uuid ? $t('label.primaryDomain') : $t('label.backupDomain') }}
                  </b-form-radio>
                </template>
              </b-table>
            </div>

            <b-pagination v-model="memberPage" :total-rows="visibleMemberRows.length" :per-page="memberPerPage"
              align="right" size="sm" class="mt-2" v-if="visibleMemberRows.length > memberPerPage"></b-pagination>

            <div class="mt-3 text-right">
              <button class="btn btn-info w-md waves-effect waves-light" type="button" @click="requestSaveMembers"
                :disabled="status.savingMembers || updatePermission != true">
                <b-spinner small v-if="status.savingMembers"></b-spinner>
                {{ $t('label.saveX', [$t('label.members')]) }}
              </button>
            </div>
          </div>
        </div>
      </div>

        </div>

        <div class="card">
          <div class="card-body">
            <h4 class="card-title">{{ $t('label.playerLink') }}</h4>
            <p class="card-title-desc">{{ $t('message.playerLinkHelp') }}</p>
            <div v-if="data.playerLink">
              <code>{{ data.playerLink }}</code>
              <a href="javascript:void(0);" class="text-info ml-2" @click="copyLink(data.playerLink)" v-b-tooltip.hover
                :title="$t('label.copy')">
                <i class="mdi mdi-content-copy"></i>
              </a>
            </div>
            <div class="text-muted" v-else>{{ $t('message.noEntryIpAssigned') }}</div>
          </div>
        </div>
      </div>
    </div>
  </Layout>
</template>
